import { useEffect, useMemo, useRef } from 'react';
import { useLinkedMedcomLetterPatient } from '@/hooks/useCareCommunications';
import { usePatient } from '@/hooks/usePatients';
import { fallbackPatientGiven } from '@/lib/medcom/replyPrefill';
import type { ParsedBundleContent } from '@/lib/medcom/parseCareCommunicationBundle';

/**
 * Svar and Videresend: when the opened letter carries no Fornavn, fill it once
 * from another letter in the thread for the same patient, or from the Clinio
 * patient the import linked to the letter. A name the user typed is kept.
 */
export function usePatientGivenFallback(input: {
  enabled: boolean;
  clinicId: string | null | undefined;
  messageId: string | null | undefined;
  family: string;
  given: string;
  setGiven: (given: string) => void;
  messages: ReadonlyArray<{ id: string; direction: 'in' | 'out' }> | undefined;
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined> | undefined;
  /** Changes when the form is cleared and prefilled again; allows one more fill. */
  resetKey?: string | number;
}): void {
  const { enabled, clinicId, messageId, family, given, setGiven, messages, letters, resetKey } =
    input;
  const done = useRef(false);
  const lastResetKey = useRef(resetKey);
  const needsFallback = enabled && !done.current && !given.trim() && Boolean(family.trim());
  const link = useLinkedMedcomLetterPatient(
    needsFallback ? clinicId : null,
    needsFallback ? messageId : null,
  );
  const { data: linkedPatient } = usePatient(needsFallback ? link.data?.patientId : null);
  const threadLetters = useMemo(
    () =>
      (messages ?? []).flatMap((item) => {
        const letter = letters?.get(item.id);
        return letter
          ? [{ direction: item.direction, family: letter.patientFamily, given: letter.patientGiven }]
          : [];
      }),
    [messages, letters],
  );

  useEffect(() => {
    if (lastResetKey.current !== resetKey) {
      lastResetKey.current = resetKey;
      done.current = false;
    }
    if (!enabled || done.current) return;
    if (given.trim()) {
      done.current = true;
      return;
    }
    const fallback = fallbackPatientGiven({
      family,
      letters: threadLetters,
      linkedPatient: linkedPatient
        ? { firstName: linkedPatient.first_name, lastName: linkedPatient.last_name }
        : null,
    });
    if (!fallback) return;
    done.current = true;
    setGiven(fallback);
  }, [enabled, given, family, threadLetters, linkedPatient, setGiven, resetKey]);
}
